<template>
    <div class="scheduled-report-form px-4" data-app="true">
        <div :style="scheduledReportBanner.isShow ? 'bottom: 0;' : 'bottom: -10rem;'" :class="scheduledReportBanner.class"
            class="report-banner" style="z-index: 51;">
            <div class="d-flex align-items-center"><b>{{ scheduledReportBanner.text }}</b></div>
        </div>
        <div class="mt-3">
            <div class="navigation-header">
                <div class="header d-flex w-100 align-items-center py-2">
                    <a :href="prevUrl" class="mr-3" @click.prevent="handleExit">
                        <v-icon dense color="#3F3F3F" size="24">mdi-arrow-left</v-icon>
                    </a>
                    <h3 class="m-0 p-0 m-0 title-header">
                        <span class="text-capitalize"><b>{{ pageTitle }}</b></span>
                    </h3>
                </div>
                <span class="sub-header d-block my-2">
                    Automated reports will be sent to the specified email address based on the schedule set below.
                </span>
            </div>
        </div>

        <!-- Stepper Header -->
        <StepperHeader :steps="steps" :currentStep="currentStep" />

        <!-- Step Contents & button stepper -->
        <div class="step-content py-3">
            <!-- Step 1: Report Template & Schedule -->
            <div v-show="currentStep === 1">
                <ReportTemplate ref="reportTemplateStep" @update:formData="handleFormDataUpdate"
                    :initial-data="isEditMode ? initialData : null" @run-report="handleRunReport" />
                <StepperNextPrevButton :prevButtonLabel="labelPrevBtn" :nextButtonLabel="labelNextBtn"
                    :currentStep="currentStep" :totalSteps="steps.length" :disableNextButton="isStep1NextDisabled"
                    @next="nextStep" @prev="prevStep"
                    :sticky="true"
                    buttonLayoutPlacement="between" />
            </div>

            <!-- Step 2: Email Settings -->
            <div v-show="currentStep === 2">
                <EmailSetting ref="emailSettingStep" v-model="scheduledReportPayload.email_settings"
                    :report-data="scheduledReportPayload" />
                <StepperNextPrevButton :prevButtonLabel="labelPrevBtn" :nextButtonLabel="labelNextBtn"
                    :currentStep="currentStep" :totalSteps="steps.length" :disableNextButton="isStep2NextDisabled"
                    @next="nextStep" @prev="prevStep"
                    :sticky="true"
                    buttonLayoutPlacement="between" />
            </div>

            <!-- Step 3: Preview -->
            <div v-show="currentStep === 3" class="step-content">
                <PreviewStep :report-data="scheduledReportPayload" />
                <div class="mt-4">
                    <StepperNextPrevButton :prevButtonLabel="labelPrevBtn" :nextButtonLabel="submitButtonLabel"
                        :currentStep="currentStep" :totalSteps="steps.length" :disableNextButton="isStep3NextDisabled"
                        @next="nextStep" @prev="prevStep"
                        :sticky="true"
                        buttonLayoutPlacement="between" />
                </div>
            </div>
        </div>

        <v-dialog v-model="unsavedModal.isOpen" max-width="616px" persistent>
            <v-card class="bg-white p-4">
                <h4 style="font-size: 24px; color: #3F3F3F;">Unsaved Changes</h4>
                <p class="m-0 my-4" style="font-size: 14px; color: #3F3F3F;">
                    Looks like you’ve made changes. Are you sure you want to leave this page?
                    <br>
                    If you leave, the updates you’ve made will be lost.
                </p>
                <div class="d-flex justify-content-between align-items-center" style="gap: 8px;">
                    <button class="btn btn-outline-primary" @click="closeUnsavedModal">Cancel</button>
                    <button class="btn btn-danger" @click="discardChanges" style="border: none;">Discard</button>
                </div>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import StepperHeader from '../shared/StepperHeader.vue';
import StepperNextPrevButton from '../shared/StepperNextPrevButton.vue';
import ReportTemplate from './step/ReportTemplate.vue';
import EmailSetting from './step/EmailSetting.vue';
import PreviewStep from "./step/PreviewStep.vue";

export default {
    name: 'ScheduledReportForm',
    components: {
        StepperHeader, StepperNextPrevButton, ReportTemplate, EmailSetting, PreviewStep
    },
    props: {
        isEditMode: {
            type: Boolean,
            default: false
        },
        initialData: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            currentStep: 1,
            steps: [
                { value: 1, label: 'Select Report Template & Schedule', icon: 'mdi-clipboard-list-outline' },
                { value: 2, label: 'Email Settings', icon: 'mdi-email-outline' },
                { value: 3, label: 'Preview', icon: 'mdi-eye-outline' }
            ],
            stepDataLoaded: {
                1: false,
                2: false,
                3: false
            },
            unsavedModal: {
                isOpen: false,
            },
            pendingRedirect: '',
            initialSnapshot: '',
            scheduledReportPayload: {
                name: '',
                category: '',
                template_id: null,
                schedule_type: 'weekly',
                schedule_day: 'monday',
                schedule_time: '09:00',
                schedule_next_run_at: null,
                schedule_last_run_at: null,
                filters: {},
                columns: [],
                email_settings: {
                    recipients: [],
                    subject: '',
                    body: '',
                    header_logo: 'fastvisa'
                },
            },
            step1FormData: null,
            allowUnload: false,
        };
    },
    computed: {
        pageTitle() {
            return this.isEditMode ? 'Edit Scheduled Report' : 'New Scheduled Report';
        },
        submitButtonLabel() {
            return this.isEditMode ? 'Update Schedule' : 'Create Schedule';
        },
        prevUrl() {
            if (this.isEditMode) {
                // Go back to detail page when editing
                const reportId = this.initialData?.id || '';
                return `/dashboard/scheduled_report/${reportId}`;
            }
            if (this.$route?.path?.includes('report_list')) {
                return '/dashboard/report_list';
            }
            return '/dashboard/scheduled_report';
        },
        labelNextBtn() {
            if (this.currentStep != this.steps.length) {
                return `Next : ${this.steps[this.currentStep].label}`;
            }
            return this.submitButtonLabel;
        },
        labelPrevBtn() {
            if (this.currentStep != 1) {
                return `Back : ${this.steps[this.currentStep - 2].label}`;
            }
            return 'Cancel';
        },
        ...mapGetters({
            selectedColumns: 'reportTemplates/selectedColumns',
            originalColumns: 'reportTemplates/originalColumns',
            reportTemplates: 'reportSchedules/reportTemplates',
            currentUser: 'currentUser',
            scheduledReportBanner: 'scheduledReportBanner',
            hasTemplateChanges: 'reportTemplates/hasUnsavedChanges',
            templateUnsavedChanges: 'reportTemplates/unsavedChanges',
        }),
        hasFormChanges() {
            if (!this.initialSnapshot) return false;
            return this.buildSnapshot() !== this.initialSnapshot;
        },
        hasUnsavedChanges() {
            return this.hasFormChanges || this.hasTemplateChanges || this.templateUnsavedChanges;
        },
        step1Data() {
            return this.step1FormData || {};
        },
        isStep1Valid() {
            const name = (this.step1Data.name || this.scheduledReportPayload.name || '').trim();
            const category = this.step1Data.category || this.scheduledReportPayload.category;
            const templateId = this.step1Data.template_id || this.scheduledReportPayload.template_id;
            return !!(name && category && templateId);
        },
        isStep2Valid() {
            const emailSettings = this.scheduledReportPayload.email_settings || {};
            const recipients = Array.isArray(emailSettings.recipients) ? emailSettings.recipients : [];
            const subject = (emailSettings.subject || '').trim();
            const bodyText = (emailSettings.body || '').replace(/<[^>]*>/g, '').trim();
            return recipients.length > 0 && !!subject && !!bodyText;
        },
        isStep1NextDisabled() {
            return !this.isStep1Valid;
        },
        isStep2NextDisabled() {
            return !this.isStep2Valid;
        },
        isStep3NextDisabled() {
            return !(this.isStep1Valid && this.isStep2Valid);
        },
    },
    watch: {
        initialData: {
            immediate: true,
            deep: true,
            handler(val) {
                if (this.isEditMode && val && (val.id || val.template_id || val.name)) {
                    this.populateFromInitialData();
                }
            }
        },
        currentStep: {
            immediate: true,
            handler(newStep) {
                this.loadStepData(newStep);
            }
        }
    },
    mounted() {
        // Pre-populate form data if in edit mode
        if (this.isEditMode && this.initialData) {
            this.populateFromInitialData();
        } else {
            this.$nextTick(() => this.syncInitialSnapshot());
        }
        document.addEventListener('click', this.handleGlobalNavigation, true);
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleGlobalNavigation, true);
    },
    methods: {
        populateFromInitialData() {
            const data = this.initialData;
            this.scheduledReportPayload = {
                ...this.scheduledReportPayload,
                id: data.id,
                name: data.name || '',
                category: data.category || '',
                template_id: data.template_id || null,
                template_name: data.template_name || '',
                schedule_id: data.schedule_id || null,
                schedule_type: data.schedule_type || 'weekly',
                schedule_day: data.schedule_day || 'monday',
                schedule_time: data.schedule_time || '09:00',
                schedule_next_run_at: data.schedule_next_run_at || null,
                schedule_last_run_at: data.schedule_last_run_at || null,
                filters: data.filters || {},
                columns: data.columns || [],
                created_by: data.created_by || '',
                email_settings: {
                    recipients: data.email_settings?.recipients || [],
                    subject: data.email_settings?.subject || '',
                    body: data.email_settings?.body || '',
                    header_logo: data.email_settings?.header_logo || 'fastvisa'
                },
            };
            this.$nextTick(() => this.syncInitialSnapshot());
        },
        buildSnapshot() {
            const stepData = this.step1FormData || {};
            const emailSettings = this.scheduledReportPayload.email_settings || {};
            const recipients = Array.isArray(emailSettings.recipients)
                ? emailSettings.recipients
                    .map((recipient) => ({
                        id: recipient?.id,
                        email: recipient?.email,
                        name: recipient?.name,
                        schedule_recipient_id: recipient?.schedule_recipient_id,
                    }))
                    .filter((recipient) => recipient.id || recipient.email || recipient.name)
                    .sort((a, b) => (a.id || a.email || '').toString().localeCompare((b.id || b.email || '').toString()))
                : [];

            const snapshot = {
                name: (stepData.name ?? this.scheduledReportPayload.name) || '',
                category: stepData.category ?? this.scheduledReportPayload.category ?? '',
                template_id: stepData.template_id ?? this.scheduledReportPayload.template_id ?? null,
                schedule_type: stepData.schedule_type ?? this.scheduledReportPayload.schedule_type ?? 'weekly',
                schedule_day: stepData.schedule_day ?? this.scheduledReportPayload.schedule_day ?? 'monday',
                schedule_time: stepData.schedule_time ?? this.scheduledReportPayload.schedule_time ?? '09:00',
                filters: stepData.filters ?? this.scheduledReportPayload.filters ?? {},
                email_settings: {
                    recipients,
                    subject: emailSettings.subject || '',
                    body: emailSettings.body || '',
                    header_logo: emailSettings.header_logo || 'fastvisa',
                },
            };

            return JSON.stringify(snapshot);
        },
        syncInitialSnapshot() {
            this.initialSnapshot = this.buildSnapshot();
        },
        openUnsavedModal(redirectUrl) {
            this.pendingRedirect = redirectUrl || '';
            this.unsavedModal.isOpen = true;
        },
        closeUnsavedModal() {
            this.unsavedModal.isOpen = false;
            this.pendingRedirect = '';
        },
        discardChanges() {
            const target = this.pendingRedirect || this.prevUrl;
            this.prepareForRedirect();
            if (target) {
                window.location.href = target;
            }
        },
        handleExit() {
            if (this.hasUnsavedChanges) {
                this.openUnsavedModal(this.prevUrl);
                return;
            }
            window.location.href = this.prevUrl;
        },
        handleGlobalNavigation(event) {
            if (!this.hasUnsavedChanges) return;
            const anchor = event.target.closest('a[href]');
            if (!anchor) return;

            // Ignore utility actions that only open in-page popover/dropdown.
            if (this.shouldIgnoreUnsavedGuardForLink(anchor)) return;

            const href = anchor.getAttribute('href');
            if (!href || href.startsWith('#') || href.startsWith('javascript:')) return;
            if (anchor.getAttribute('target') === '_blank' || anchor.hasAttribute('download')) return;
            if (href.startsWith('mailto:') || href.startsWith('tel:')) return;
            event.preventDefault();
            event.stopPropagation();
            this.openUnsavedModal(href);
        },
        shouldIgnoreUnsavedGuardForLink(anchor) {
            const ignoredLinkIds = ['process-logs-btn', 'notification-btn', 'new-item-link'];
            if (ignoredLinkIds.includes(anchor.id)) return true;

            const toggle = (anchor.getAttribute('data-toggle') || '').toLowerCase();
            return ['popover', 'dropdown', 'modal'].includes(toggle);
        },
        prepareForRedirect() {
            this.allowUnload = true;
            this.closeUnsavedModal();
            this.syncInitialSnapshot();
            document.removeEventListener('click', this.handleGlobalNavigation, true);
        },
        async loadStepData(step) {
            if (this.stepDataLoaded[step]) return;
            this.stepDataLoaded[step] = true;
        },
        handleFormDataUpdate(formData) {
            this.step1FormData = formData;
        },
        handleRunReport() {
            this.$store.commit('setScheduledReportBannerSuccess', 'Document is being processed, check progress on process log!');
            this.$emit('run-report');
        },
        collectStepData() {
            const formData = this.step1FormData || {};
            const columnChanges = this.selectedColumns.map(col => ({
                id: col.id,
                columnName: col.columnName,
                header_name: col.header_name || col.original_header_name,
                original_header_name: col.original_header_name,
                group: col.group,
                position: col.position,
                _destroy: col._destroy || 0,
            }));

            this.scheduledReportPayload = {
                ...this.scheduledReportPayload,
                name: formData.name || '',
                category: formData.category || '',
                template_id: formData.template_id || null,
                schedule_type: formData.schedule_type || 'weekly',
                schedule_day: formData.schedule_day || '',
                schedule_time: formData.schedule_time || '',
                // If user changes schedule settings during edit, ignore stale next_run_at
                schedule_next_run_at: this.isEditMode && this.initialData && (
                    (formData.schedule_type || 'weekly') !== (this.initialData.schedule_type || 'weekly') ||
                    (formData.schedule_day || '') !== (this.initialData.schedule_day || '') ||
                    (formData.schedule_time || '') !== (this.initialData.schedule_time || '')
                )
                    ? null
                    : this.scheduledReportPayload.schedule_next_run_at,
                schedule_last_run_at: this.scheduledReportPayload.schedule_last_run_at,
                filters: formData.filters || {},
                columns: columnChanges,
            };

            const template = this.reportTemplates.find(t => t.id === this.scheduledReportPayload.template_id);
            if (template) {
                this.scheduledReportPayload.template_name = template.name;
            }

            if (this.currentUser) {
                this.scheduledReportPayload.created_by = this.currentUser.name || this.currentUser.email;
            }
        },
        nextStep() {
            if (this.currentStep === 1) {
                if (this.$refs.reportTemplateStep && !this.$refs.reportTemplateStep.validate()) {
                    return;
                }
                this.collectStepData();
            }

            if (this.currentStep === 2) {
                if (this.$refs.emailSettingStep && !this.$refs.emailSettingStep.validate()) {
                    return;
                }
            }

            if (this.currentStep === this.steps.length) {
                this.submitReport();
                return;
            }

            if (this.currentStep < this.steps.length) {
                this.currentStep++;
            }
        },
        submitReport() {
            this.$emit('submit', this.scheduledReportPayload);
        },
        prevStep() {
            if (this.currentStep > 1) {
                this.currentStep--;
                return;
            }
            this.handleExit();
        },
        goToStep(step) {
            if (this.currentStep === 1 && step > 1) {
                this.collectStepData();
            }
            this.currentStep = step;
        }
    }
}
</script>

<style>
.scheduled-report-form .step-content {
    padding-bottom: 128px;
}

.title-header {
    font-size: 24px;
    font-weight: 600;
}

.sub-header {
    font-size: 14px;
    font-weight: 400;
}

.report-banner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: fixed;
    font-size: 1em;
    left: 5rem;
    right: 2rem;
    width: 100%;
    transition: 1s;
    padding: 0 2rem;
    height: 4.5rem;
    font-family: 'Lato';
    box-sizing: border-box;
}

.bg-green-banner {
    background: #E5FFEC !important;
}

.bg-red-banner {
    background: #FFEDED !important;
}

.text-red-banner {
    color: #D43F3A !important;
}
</style>
